import { Loader2, Search } from "lucide-react";
import { useMemo } from "react";
import { useSearchParams } from "react-router";

import { AppHeader } from "@/components/app-header";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { useCases } from "@/lib/api";

import { CaseTable } from "./case-table";

const INDUSTRIES = [
  "Consumer",
  "Healthcare",
  "Financial services",
  "Public sector",
  "Energy",
  "Technology",
];
const SERVICE_LINES = ["Strategy", "Innovation", "Design", "Product", "Venture", "Research"];
const REGIONS = ["EMEA", "Americas", "APAC"];
const STAGES = ["Draft", "Approved"];

interface FilterSelectProps {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
}

function FilterSelect({ label, value, options, onChange }: FilterSelectProps) {
  return (
    <Select aria-label={label} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{label}: All</option>
      {options.map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
    </Select>
  );
}

export function CasesPage() {
  const casesQuery = useCases();
  const [searchParams, setSearchParams] = useSearchParams();

  const q = searchParams.get("q") ?? "";
  const industry = searchParams.get("industry") ?? "";
  const serviceLine = searchParams.get("service_line") ?? "";
  const region = searchParams.get("region") ?? "";
  const year = searchParams.get("year") ?? "";
  const stage = searchParams.get("stage") ?? "";

  const setParam = (key: string, value: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) {
          next.set(key, value);
        } else {
          next.delete(key);
        }
        return next;
      },
      { replace: true },
    );
  };

  const cases = useMemo(() => casesQuery.data ?? [], [casesQuery.data]);

  const years = useMemo(() => {
    const set = new Set<number>();
    for (const c of cases) {
      if (c.year !== null) {
        set.add(c.year);
      }
    }
    return [...set].sort((a, b) => b - a).map(String);
  }, [cases]);

  const filtered = useMemo(() => {
    const text = q.trim().toLowerCase();
    return cases.filter((c) => {
      if (
        text &&
        !c.client_name.toLowerCase().includes(text) &&
        !c.slide_title.toLowerCase().includes(text)
      ) {
        return false;
      }
      if (industry && !c.industry.includes(industry)) {
        return false;
      }
      if (serviceLine && !c.service_lines.includes(serviceLine)) {
        return false;
      }
      if (region && c.region !== region) {
        return false;
      }
      if (year && String(c.year ?? "") !== year) {
        return false;
      }
      if (stage && c.stage !== stage) {
        return false;
      }
      return true;
    });
  }, [cases, q, industry, serviceLine, region, year, stage]);

  const hasActiveFilters = Boolean(q || industry || serviceLine || region || year || stage);

  return (
    <div className="bg-background min-h-screen">
      <AppHeader />
      <main className="mx-auto max-w-6xl px-6 py-10">
        <section className="mb-8">
          <h1 className="font-heading text-primary text-3xl font-bold">
            Casey&rsquo;s Cases Library
          </h1>
          <p className="text-muted mt-2 max-w-2xl text-sm leading-6">
            Case studies auto-drafted by Casey when projects close — review, refine, and publish
            them from here.
          </p>
        </section>

        <div className="mb-6 grid gap-3 md:grid-cols-3 lg:grid-cols-6">
          <div className="relative md:col-span-3 lg:col-span-1">
            <Search className="text-muted absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
            <Input
              placeholder="Search client or title…"
              className="pl-9"
              value={q}
              onChange={(e) => setParam("q", e.target.value)}
            />
          </div>
          <FilterSelect
            label="Industry"
            value={industry}
            options={INDUSTRIES}
            onChange={(v) => setParam("industry", v)}
          />
          <FilterSelect
            label="Service line"
            value={serviceLine}
            options={SERVICE_LINES}
            onChange={(v) => setParam("service_line", v)}
          />
          <FilterSelect
            label="Region"
            value={region}
            options={REGIONS}
            onChange={(v) => setParam("region", v)}
          />
          <FilterSelect
            label="Year"
            value={year}
            options={years}
            onChange={(v) => setParam("year", v)}
          />
          <FilterSelect
            label="Stage"
            value={stage}
            options={STAGES}
            onChange={(v) => setParam("stage", v)}
          />
        </div>

        {casesQuery.isLoading ? (
          <div className="border-border rounded-lg border">
            <div className="animate-pulse space-y-0">
              {Array.from({ length: 6 }, (_, i) => (
                <div key={i} className="border-border flex gap-6 border-b px-4 py-4 last:border-0">
                  <div className="bg-primary/10 h-4 w-1/3 rounded" />
                  <div className="bg-primary/10 h-4 w-1/6 rounded" />
                  <div className="bg-primary/10 h-4 w-1/6 rounded" />
                  <div className="bg-primary/10 h-4 w-1/6 rounded" />
                </div>
              ))}
            </div>
          </div>
        ) : casesQuery.isError ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted text-sm">
                Could not load the cases library.{" "}
                <button
                  type="button"
                  onClick={() => void casesQuery.refetch()}
                  className="underline"
                >
                  Try again
                </button>
              </p>
            </CardContent>
          </Card>
        ) : filtered.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
              <p className="font-heading text-primary text-lg font-bold">
                {hasActiveFilters ? "No cases match these filters" : "No cases yet"}
              </p>
              <p className="text-muted max-w-md text-sm">
                {hasActiveFilters
                  ? "Try broadening your search or clearing a filter."
                  : "Cases generated by Casey will show up here once a project closes."}
              </p>
              {hasActiveFilters && (
                <Button variant="outline" size="sm" onClick={() => setSearchParams({})}>
                  Clear filters
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <>
            <p className="text-muted mb-3 flex items-center gap-2 text-sm">
              {casesQuery.isFetching && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {filtered.length} {filtered.length === 1 ? "case" : "cases"}
            </p>
            <CaseTable cases={filtered} />
          </>
        )}
      </main>
    </div>
  );
}
